/**
 * The shell both legal documents share.
 *
 * Deliberately outside the application frame: Google's OAuth reviewer opens these URLs in a
 * browser with no session, and a privacy policy that redirects to a sign-in screen fails
 * verification. There is no nav, no data fetching and no auth — just the document, a way
 * back, and the operator's name.
 */
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import { ProductMark } from '@/components/shell/Sidebar'
import { env } from '@/config/env'

export function LegalDocument({
  title,
  summary,
  children,
}: {
  title: string
  summary: string
  children: ReactNode
}) {
  return (
    <div className="min-h-full bg-ground">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <ProductMark className="h-7 w-7 shrink-0" />
            <span className="text-sm font-semibold text-ink">RepoPilot</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/privacy" className="text-ink-muted hover:text-ink">
              Privacy
            </Link>
            <Link to="/terms" className="text-ink-muted hover:text-ink">
              Terms
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        <p className="mt-2 text-base text-ink-muted">{summary}</p>
        <p className="mt-4 text-xs text-ink-faint">
          Last updated {env.legal.updatedAt} · Operated by {env.legal.operator}
        </p>

        <div className="mt-8 space-y-8">{children}</div>

        <footer className="mt-12 border-t border-line pt-6 text-sm text-ink-muted">
          <p>
            Questions about this document?{' '}
            {env.legal.contactEmail ? (
              <a
                href={`mailto:${env.legal.contactEmail}`}
                className="font-medium text-primary-bright underline-offset-2 hover:underline"
              >
                {env.legal.contactEmail}
              </a>
            ) : (
              <span>
                Contact the operator of this deployment. (Set VITE_LEGAL_CONTACT_EMAIL to show
                an address here.)
              </span>
            )}
          </p>
          <Link
            to="/"
            className="mt-3 inline-block text-primary-bright underline-offset-2 hover:underline"
          >
            Back to the application
          </Link>
        </footer>
      </main>
    </div>
  )
}

/** One numbered-free section. Headings carry the structure; the content is prose. */
export function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-md font-semibold text-ink">{heading}</h2>
      <div className="mt-3 space-y-3 text-base leading-relaxed text-ink-dim">{children}</div>
    </section>
  )
}

export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="ml-5 list-disc space-y-2">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}
